import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Button, Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";
import { cx } from "@/lib/cx";
import { inputClass, selectClass } from "@/lib/forms";
import type {
  ModelTemplate,
  ModelTemplateAccessory,
  ModelTemplateField,
} from "@/modules/plus/plus.schema";
import { atoms } from "@/state";

// Editing target: either the existing template (edit mode) or null (create a
// new template for `modelCode`). Setting the atom opens the dialog; setting to
// null closes it.
export interface TemplateEditTarget {
  modelCode: string;
  initial: ModelTemplate | null;
}

export const templateEditAtom = atom<TemplateEditTarget | null>(null);

export const useOpenTemplateEditor = () => {
  const set = useSetAtom(templateEditAtom);
  return (target: TemplateEditTarget) => set(target);
};

// --- internal form model ----------------------------------------------------
// The form represents `codes` as a list of {code, meaning} rows so the user
// can add/remove entries in order. Converted to a Record<string,string> on save.

type FieldKind = "codes" | "values";

interface CodeRow {
  code: string;
  meaning: string;
}

interface FieldDraft {
  position: string; // string in the form, parsed to number if numeric on save
  name: string;
  kind: FieldKind;
  codeRows: CodeRow[];
  values: string;
  note: string;
}

interface AccessoryDraft {
  code: string;
  meaning: string;
  category: string;
}

interface FormState {
  series_id: string;
  heading: string;
  example: string;
  catalog_page: string;
  fields: FieldDraft[];
  accessories: AccessoryDraft[];
  notesText: string; // one bullet per line
}

const emptyField = (position = 1): FieldDraft => ({
  position: String(position),
  name: "",
  kind: "codes",
  codeRows: [{ code: "", meaning: "" }],
  values: "",
  note: "",
});

const emptyAccessory = (): AccessoryDraft => ({ code: "", meaning: "", category: "" });

const initialFor = (target: TemplateEditTarget): FormState => {
  const t = target.initial;
  if (!t) {
    return {
      series_id: target.modelCode,
      heading: "",
      example: "",
      catalog_page: "",
      fields: [emptyField(1)],
      accessories: [],
      notesText: "",
    };
  }
  return {
    series_id: t.series_id,
    heading: t.heading,
    example: t.example,
    catalog_page: t.catalog_page ?? "",
    fields: t.fields.map((f) => ({
      position: String(f.position),
      name: f.name,
      kind: f.codes ? "codes" : "values",
      codeRows: f.codes
        ? Object.entries(f.codes).map(([code, meaning]) => ({ code, meaning }))
        : [{ code: "", meaning: "" }],
      values: f.values ?? "",
      note: f.note ?? "",
    })),
    accessories: (t.accessories ?? []).map((a) => ({ ...a })),
    notesText: (t.notes ?? []).join("\n"),
  };
};

const toTemplate = (form: FormState): ModelTemplate => {
  const fields: ModelTemplateField[] = form.fields.map((f) => {
    const positionNumber = Number(f.position);
    const position =
      Number.isFinite(positionNumber) && String(positionNumber) === f.position.trim()
        ? positionNumber
        : f.position.trim();
    const base: ModelTemplateField = { position, name: f.name.trim() };
    if (f.kind === "codes") {
      const codes: Record<string, string> = {};
      for (const row of f.codeRows) {
        const c = row.code.trim();
        if (c) {
          codes[c] = row.meaning.trim();
        }
      }
      if (Object.keys(codes).length) {
        base.codes = codes;
      }
    } else if (f.values.trim()) {
      base.values = f.values.trim();
    }
    if (f.note.trim()) {
      base.note = f.note.trim();
    }
    return base;
  });

  const accessories: ModelTemplateAccessory[] = form.accessories
    .map((a) => ({ code: a.code.trim(), meaning: a.meaning.trim(), category: a.category.trim() }))
    .filter((a) => a.code);

  const notes = form.notesText
    .split("\n")
    .map((n) => n.trim())
    .filter(Boolean);

  const template: ModelTemplate = {
    series_id: form.series_id.trim(),
    heading: form.heading.trim(),
    example: form.example.trim(),
    fields,
  };
  if (form.catalog_page.trim()) {
    template.catalog_page = form.catalog_page.trim();
  }
  if (accessories.length) {
    template.accessories = accessories;
  }
  if (notes.length) {
    template.notes = notes;
  }
  return template;
};

// --- UI primitives ---------------------------------------------------------

const labelClass = "text-xs font-medium text-gray-700 dark:text-gray-300";

// --- main dialog -----------------------------------------------------------

export const EditTemplateDialog: React.FC = () => {
  const [target, setTarget] = useAtom(templateEditAtom);
  const saveMutation = useAtomValue(atoms.data.saveTemplate);
  const fieldNames = useAtomValue(atoms.suggestions.fieldNames);
  const codesByFieldName = useAtomValue(atoms.suggestions.codesByFieldName);
  const meaningsByCode = useAtomValue(atoms.suggestions.meaningsByCode);
  const accessoryCategories = useAtomValue(atoms.suggestions.accessoryCategories);
  const catalogPages = useAtomValue(atoms.suggestions.catalogPages);
  const allCodes = useAtomValue(atoms.suggestions.allCodes);
  const [form, setForm] = useState<FormState | null>(null);
  const isOpen = target !== null;

  // Re-initialize the form whenever the target changes. Avoid stale form state
  // when the user closes one editor and opens another.
  useEffect(() => {
    setForm(target ? initialFor(target) : null);
  }, [target]);

  if (!form) {
    return null;
  }

  const close = () => setTarget(null);

  const onSubmit = async () => {
    const template = toTemplate(form);
    if (!template.series_id || !template.fields.length) {
      toast.error("Series ID and at least one field are required");
      return;
    }
    try {
      await saveMutation.mutateAsync(template);
      toast.success(`Saved template for ${template.series_id}`);
      close();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    }
  };

  const updateField = (idx: number, patch: Partial<FieldDraft>) => {
    setForm({ ...form, fields: form.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)) });
  };
  const removeField = (idx: number) => {
    setForm({ ...form, fields: form.fields.filter((_, i) => i !== idx) });
  };
  const addField = () => {
    setForm({ ...form, fields: [...form.fields, emptyField(form.fields.length + 1)] });
  };

  const updateCodeRow = (fieldIdx: number, rowIdx: number, patch: Partial<CodeRow>) => {
    updateField(fieldIdx, {
      codeRows: form.fields[fieldIdx]!.codeRows.map((r, i) =>
        i === rowIdx ? { ...r, ...patch } : r,
      ),
    });
  };
  const removeCodeRow = (fieldIdx: number, rowIdx: number) => {
    updateField(fieldIdx, {
      codeRows: form.fields[fieldIdx]!.codeRows.filter((_, i) => i !== rowIdx),
    });
  };
  const addCodeRow = (fieldIdx: number) => {
    updateField(fieldIdx, {
      codeRows: [...form.fields[fieldIdx]!.codeRows, { code: "", meaning: "" }],
    });
  };

  const updateAccessory = (idx: number, patch: Partial<AccessoryDraft>) => {
    setForm({
      ...form,
      accessories: form.accessories.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    });
  };
  const removeAccessory = (idx: number) => {
    setForm({ ...form, accessories: form.accessories.filter((_, i) => i !== idx) });
  };
  const addAccessory = () => {
    setForm({ ...form, accessories: [...form.accessories, emptyAccessory()] });
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
      isDismissable
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <Modal className="h-full max-h-[90vh] w-full max-w-3xl rounded-2xl bg-gray-50 shadow-xl outline-none dark:bg-gray-950">
        <Dialog className="flex h-full flex-col outline-none">
          <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 dark:border-gray-800 dark:bg-gray-900">
            <h2 className="text-lg font-semibold dark:text-gray-100">
              {target?.initial ? "Edit template" : "Build a template"}{" "}
              <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                — {target?.modelCode}
              </span>
            </h2>
            <Button
              onPress={close}
              className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Close
            </Button>
          </header>

          {/* Dialog-wide suggestion lists. <datalist> can live anywhere in the
              tree; siblings can reference them by id. Per-field/row dynamic
              lists live inline with their rows. */}
          <datalist id="dl-field-names">
            {fieldNames.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <datalist id="dl-accessory-categories">
            {accessoryCategories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <datalist id="dl-catalog-pages">
            {catalogPages.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          <datalist id="dl-all-codes">
            {allCodes.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="flex flex-col gap-6">
              {/* Header block */}
              <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label htmlFor="g-series" className={labelClass}>
                    Series ID *
                  </label>
                  <input
                    id="g-series"
                    value={form.series_id}
                    onChange={(e) => setForm({ ...form, series_id: e.target.value })}
                    className={cx(inputClass, "font-mono")}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="g-catalog" className={labelClass}>
                    Catalog page
                  </label>
                  <input
                    id="g-catalog"
                    list="dl-catalog-pages"
                    value={form.catalog_page}
                    onChange={(e) => setForm({ ...form, catalog_page: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <label htmlFor="g-heading" className={labelClass}>
                    Heading
                  </label>
                  <input
                    id="g-heading"
                    value={form.heading}
                    onChange={(e) => setForm({ ...form, heading: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <label htmlFor="g-example" className={labelClass}>
                    Example code
                  </label>
                  <input
                    id="g-example"
                    value={form.example}
                    onChange={(e) => setForm({ ...form, example: e.target.value })}
                    placeholder="6500 - O - 9 x 9 - 24 x 24 - L - AW - 4A"
                    className={cx(inputClass, "font-mono")}
                  />
                </div>
              </section>

              {/* Fields */}
              <section className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Fields
                  </h3>
                  <button
                    type="button"
                    onClick={addField}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    + Add field
                  </button>
                </div>
                {form.fields.map((f, fi) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: form drafts are positional, no stable identity
                    key={fi}
                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        Field {fi + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeField(fi)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[80px_1fr_auto]">
                      <div className="flex flex-col gap-1">
                        <label htmlFor={`f-${fi}-position`} className={labelClass}>
                          Position
                        </label>
                        <input
                          id={`f-${fi}-position`}
                          value={f.position}
                          onChange={(e) => updateField(fi, { position: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label htmlFor={`f-${fi}-name`} className={labelClass}>
                          Name
                        </label>
                        <input
                          id={`f-${fi}-name`}
                          list="dl-field-names"
                          value={f.name}
                          onChange={(e) => updateField(fi, { name: e.target.value })}
                          placeholder="e.g. Frame Type"
                          className={inputClass}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label htmlFor={`f-${fi}-kind`} className={labelClass}>
                          Kind
                        </label>
                        <select
                          id={`f-${fi}-kind`}
                          value={f.kind}
                          onChange={(e) => updateField(fi, { kind: e.target.value as FieldKind })}
                          className={selectClass}
                        >
                          <option value="codes">Codes (enum)</option>
                          <option value="values">Values (free-text)</option>
                        </select>
                      </div>
                    </div>

                    {f.kind === "codes" ? (
                      <div className="mt-3 flex flex-col gap-2">
                        <div className="text-xs font-medium text-gray-700">Codes</div>
                        {/* Per-field datalist: codes that other templates use under the same field name. */}
                        <datalist id={`dl-codes-${fi}`}>
                          {(codesByFieldName[f.name.trim()] ?? []).map((c) => (
                            <option key={c} value={c} />
                          ))}
                        </datalist>
                        {f.codeRows.map((row, ri) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional drafts, no stable identity
                          <div key={ri} className="flex items-center gap-2">
                            <input
                              list={`dl-codes-${fi}`}
                              value={row.code}
                              onChange={(e) => updateCodeRow(fi, ri, { code: e.target.value })}
                              placeholder="Code"
                              className={cx(inputClass, "w-28 font-mono")}
                            />
                            <input
                              list={`dl-meaning-${fi}-${ri}`}
                              value={row.meaning}
                              onChange={(e) => updateCodeRow(fi, ri, { meaning: e.target.value })}
                              placeholder="Meaning"
                              className={cx(inputClass, "flex-1")}
                            />
                            {/* Per-row datalist: meanings already used for this exact code. */}
                            <datalist id={`dl-meaning-${fi}-${ri}`}>
                              {(meaningsByCode[row.code.trim()] ?? []).map((m) => (
                                <option key={m} value={m} />
                              ))}
                            </datalist>
                            <button
                              type="button"
                              onClick={() => removeCodeRow(fi, ri)}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addCodeRow(fi)}
                          className="self-start text-xs text-blue-600 hover:underline"
                        >
                          + Add code
                        </button>
                      </div>
                    ) : (
                      <div className="mt-3 flex flex-col gap-1">
                        <label htmlFor={`f-${fi}-values`} className={labelClass}>
                          Values description
                        </label>
                        <textarea
                          id={`f-${fi}-values`}
                          rows={2}
                          value={f.values}
                          onChange={(e) => updateField(fi, { values: e.target.value })}
                          placeholder="e.g. Imperial (inches): 12 x 12, 24 x 24 (default). Metric (mm): 300 x 300"
                          className={inputClass}
                        />
                      </div>
                    )}

                    <div className="mt-3 flex flex-col gap-1">
                      <label htmlFor={`f-${fi}-note`} className={labelClass}>
                        Note (optional)
                      </label>
                      <input
                        id={`f-${fi}-note`}
                        value={f.note}
                        onChange={(e) => updateField(fi, { note: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                ))}
              </section>

              {/* Accessories */}
              <section className="flex flex-col gap-3">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Accessories
                  </h3>
                  <button
                    type="button"
                    onClick={addAccessory}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    + Add accessory
                  </button>
                </div>
                {form.accessories.length === 0 && (
                  <p className="text-xs text-gray-500">
                    None — accessory codes appear after the positional fields and don't have a fixed
                    slot.
                  </p>
                )}
                {form.accessories.map((a, ai) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional drafts, no stable identity
                    key={ai}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-800 dark:bg-gray-900"
                  >
                    <input
                      list="dl-all-codes"
                      value={a.code}
                      onChange={(e) => updateAccessory(ai, { code: e.target.value })}
                      placeholder="Code"
                      className={cx(inputClass, "w-28 font-mono")}
                    />
                    <input
                      list={`dl-acc-meaning-${ai}`}
                      value={a.meaning}
                      onChange={(e) => updateAccessory(ai, { meaning: e.target.value })}
                      placeholder="Meaning"
                      className={cx(inputClass, "flex-1")}
                    />
                    <datalist id={`dl-acc-meaning-${ai}`}>
                      {(meaningsByCode[a.code.trim()] ?? []).map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                    <input
                      list="dl-accessory-categories"
                      value={a.category}
                      onChange={(e) => updateAccessory(ai, { category: e.target.value })}
                      placeholder="Category"
                      className={cx(inputClass, "w-40")}
                    />
                    <button
                      type="button"
                      onClick={() => removeAccessory(ai)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </section>

              {/* Notes */}
              <section className="flex flex-col gap-1">
                <label
                  htmlFor="g-notes"
                  className={cx(
                    labelClass,
                    "text-sm font-semibold uppercase tracking-wide text-gray-500",
                  )}
                >
                  Notes
                </label>
                <p className="text-xs text-gray-500">One per line.</p>
                <textarea
                  id="g-notes"
                  rows={3}
                  value={form.notesText}
                  onChange={(e) => setForm({ ...form, notesText: e.target.value })}
                  className={inputClass}
                />
              </section>
            </div>
          </div>

          <footer className="flex justify-end gap-2 border-t border-gray-200 bg-white px-6 py-3 dark:border-gray-800 dark:bg-gray-900">
            <Button
              onPress={close}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              isDisabled={saveMutation.isPending}
              onPress={onSubmit}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? "Saving…" : "Save template"}
            </Button>
          </footer>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
